import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Form, Modal, Nav, Spinner, Tab, Table } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  listErgoDictionaryItems,
  resolveErgoDictionaryItem,
  selectErgoById,
  updateErgo,
  upsertErgoDictionaryItem,
} from 'services/ergo';
import history from 'services/history';
import 'styles/laudo-base.css';
import ListaDicionario from 'components/laudos/ListaDicionario';
import useSWR, { mutate } from 'swr';

const LEADS_13 = [
  'MC5m',
  'D1m',
  'D2m',
  'D3m',
  'AVRm',
  'AVLm',
  'AVFm',
  'V1m',
  'V2m',
  'V3m',
  'V4m',
  'V5m',
  'V6m',
];
const LEADS_EXTRA = ['V3R', 'V4R', 'V5R'];
const ALL_LEADS = [...LEADS_13, ...LEADS_EXTRA];

const bottomTabList = [
  { key: 'dados-pessoais', label: 'Dados Pessoais' },
  { key: 'dados-clinicos', label: 'Dados Clínicos' },
  { key: 'avaliacoes', label: 'Avaliações' },
  { key: 'alt-ecg', label: 'Alt ECG' },
  { key: 'conclusoes', label: 'Conclusões' },
  { key: 'observacao', label: 'Observação' },
];

const tabHeadingMap = {
  'dados-pessoais': 'Dados Pessoais',
  'dados-clinicos': 'Dados Clínicos',
  avaliacoes: 'Avaliações',
  'alt-ecg': 'Alterações Eletrocardiográficas',
  conclusoes: 'Conclusões',
  observacao: 'Observação',
};

const listConfig = {
  indicacoes: { title: 'Indicações do Teste', group: 'indicacoes' },
  queixas: { title: 'Queixas', group: 'queixas' },
  medicamentos: { title: 'Medicamentos', group: 'medicamentos' },
  fatoresRisco: { title: 'Fatores de Risco', group: 'fatoresRisco' },
  atividadeFisica: { title: 'Atividade Física', group: 'atividadeFisica' },
  ecgBasal: { title: 'Eletrocardiograma Basal', group: 'ecgBasal' },
  arritmias: { title: 'Arritmias', group: 'arritmias' },
  comportamentoPA: {
    title: 'Comportamento da Pressão Arterial',
    group: 'comportamentoPA',
  },
  comentarios: { title: 'Comentários', group: 'comentarios' },
  conclusoesList: { title: 'Conclusões', group: 'conclusoes' },
};

const textListKeys = Object.keys(listConfig);

const dictionaryTabList = [
  { key: 'indicacoes', label: 'Indicações' },
  { key: 'queixas', label: 'Queixas' },
  { key: 'medicamentos', label: 'Medicamentos' },
  { key: 'fatoresRisco', label: 'Fatores de Risco' },
  { key: 'atividadeFisica', label: 'Atividade Física' },
  { key: 'ecgBasal', label: 'ECG Basal' },
  { key: 'altECG', label: 'Alt ECG' },
  { key: 'arritmias', label: 'Arritmias' },
  { key: 'comportamentoPA', label: 'Comportamento PA' },
  { key: 'comentarios', label: 'Comentários' },
  { key: 'conclusoes', label: 'Conclusões' },
];

const dictionaryEnabled = true;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeCode(value) {
  return String(value ?? '').trim();
}

function normalizeListItem(item) {
  if (!item) return { COD: '', DESCRICAO: '' };
  return {
    COD: normalizeCode(item.COD ?? item.CODIGO ?? item.cod ?? ''),
    DESCRICAO: String(item.DESCRICAO ?? item.FRASE ?? item.descricao ?? item.frase ?? ''),
  };
}

function buildInitialAltECG(ergo) {
  const row = Array.isArray(ergo?.alteracoesECG) ? ergo.alteracoesECG[0] || {} : {};
  const altECG = {};
  ALL_LEADS.forEach((lead) => {
    ['A', 'E', 'D'].forEach((phase) => {
      const key = `${lead}${phase}`; // e.g. "MC5mA"
      const codKey = `Cod${key}`; // e.g. "CodMC5mA" — raw integer FK from DB
      altECG[key] = {
        cod: String(row[codKey] ?? ''),
        desc: String(row[key] ?? ''),
      };
    });
  });
  return altECG;
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function FormErgo() {
  const { id } = useParams();
  const {
    data: ergoData,
    isLoading,
    error,
  } = useSWR(id ? ['ergo', id] : null, () => selectErgoById(id));

  const [formData, setFormData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('dados-pessoais');
  const [altECGCanais, setAltECGCanais] = useState(13);
  const [resolvingRows, setResolvingRows] = useState({});
  const dictionaryCacheRef = useRef(new Map());

  // Modal dicionário
  const [showDictionaryModal, setShowDictionaryModal] = useState(false);
  const [activeDictionaryTab, setActiveDictionaryTab] = useState(dictionaryTabList[0].key);
  const [dictionaryData, setDictionaryData] = useState({});
  const [dictionaryLoading, setDictionaryLoading] = useState({});
  const [dictionaryDraft, setDictionaryDraft] = useState({});
  const [savingDictionaryKey, setSavingDictionaryKey] = useState('');

  // O objeto principal do exame vem direto (não está aninhado como carotida)
  const ergo = ergoData;

  // ── Inicializar formData quando dados chegarem ──────────────────────────────
  useEffect(() => {
    if (!ergo) return;

    const next = {
      EXAMEFISICO: String(ergo.EXAMEFISICO ?? ''),
      FCDEITADO: String(ergo.FCDEITADO ?? ''),
      PADEITADO: String(ergo.PADEITADO ?? ''),
      FCPE: String(ergo.FCPE ?? ''),
      PAPE: String(ergo.PAPE ?? ''),
      FREQMAX: String(ergo.FREQMAX ?? ''),
      FREQSUB: String(ergo.FREQSUB ?? ''),
      PAHIPER: String(ergo.PAHIPER ?? ''),
      OBS: String(ergo.OBS ?? ''),
      altECG: buildInitialAltECG(ergo),
    };

    textListKeys.forEach((key) => {
      const apiKey = key === 'conclusoesList' ? 'conclusoes' : key;
      const raw = Array.isArray(ergo[apiKey]) ? ergo[apiKey] : [];
      next[key] = raw.map(normalizeListItem);
    });

    setFormData(next);
  }, [ergo]);

  // ── Resolver códigos dos TextLists ─────────────────────────────────────────
  const resolveDictionaryValue = useCallback(async ({ group, code }) => {
    if (!dictionaryEnabled) return null;
    const normalizedCode = normalizeCode(code);
    const normalizedGroup = normalizeCode(group);
    if (!normalizedCode || !normalizedGroup) return null;

    const cacheKey = `${normalizedGroup}|${normalizedCode}`;
    if (dictionaryCacheRef.current.has(cacheKey)) {
      return dictionaryCacheRef.current.get(cacheKey);
    }

    const resolved = await resolveErgoDictionaryItem({
      grupo: normalizedGroup,
      codigo: normalizedCode,
    });

    dictionaryCacheRef.current.set(cacheKey, resolved || null);
    return resolved;
  }, []);

  useEffect(() => {
    if (!dictionaryEnabled || !formData) return;
    let cancelled = false;

    const hydrate = async () => {
      const updates = [];

      for (const key of textListKeys) {
        const group = listConfig[key]?.group;
        if (!group) continue;
        const items = Array.isArray(formData[key]) ? formData[key] : [];

        for (let i = 0; i < items.length; i += 1) {
          const item = items[i] || {};
          const code = normalizeCode(item.COD);
          const currentText = String(item.DESCRICAO ?? '').trim();
          if (!code || currentText) continue;

          const resolved = await resolveDictionaryValue({ group, code });
          if (resolved?.descricao) {
            updates.push({
              key,
              index: i,
              patch: {
                DESCRICAO: resolved.descricao,
                COD: resolved.codigo || code,
              },
            });
          }
        }
      }

      if (cancelled || updates.length === 0) return;

      setFormData((prev) => {
        if (!prev) return prev;
        const next = { ...prev };
        updates.forEach(({ key, index, patch }) => {
          const list = Array.isArray(next[key]) ? [...next[key]] : [];
          if (!list[index]) return;
          list[index] = { ...list[index], ...patch };
          next[key] = list;
        });
        return next;
      });
    };

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [formData, resolveDictionaryValue]);

  // ── Helpers de estado ──────────────────────────────────────────────────────
  const setField = (field, value) => {
    setFormData((prev) => ({ ...(prev || {}), [field]: value }));
  };

  const setListItem = (listKey, index, field, value) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = [...(Array.isArray(prev[listKey]) ? prev[listKey] : [])];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [listKey]: list };
    });
  };

  const setListItemPatch = (listKey, index, patch) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = [...(Array.isArray(prev[listKey]) ? prev[listKey] : [])];
      if (!list[index]) return prev;
      list[index] = { ...list[index], ...patch };
      return { ...prev, [listKey]: list };
    });
  };

  const addListItem = (listKey) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = [...(Array.isArray(prev[listKey]) ? prev[listKey] : [])];
      list.push({ COD: '', DESCRICAO: '' });
      return { ...prev, [listKey]: list };
    });
  };

  const removeListItem = (listKey, index) => {
    setFormData((prev) => {
      if (!prev) return prev;
      const list = [...(Array.isArray(prev[listKey]) ? prev[listKey] : [])];
      list.splice(index, 1);
      return { ...prev, [listKey]: list };
    });
  };

  const setAltECGCell = (field, subField, value) => {
    setFormData((prev) => ({
      ...prev,
      altECG: {
        ...(prev?.altECG || {}),
        [field]: {
          ...(prev?.altECG?.[field] || { cod: '', desc: '' }),
          [subField]: value,
        },
      },
    }));
  };

  const handleResolveAltECGCode = async (field, code) => {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) {
      setFormData((prev) => ({
        ...prev,
        altECG: {
          ...(prev?.altECG || {}),
          [field]: { cod: '', desc: '' },
        },
      }));
      return;
    }
    try {
      const resolved = await resolveDictionaryValue({
        group: 'altecg',
        code: normalizedCode,
      });
      setFormData((prev) => ({
        ...prev,
        altECG: {
          ...(prev?.altECG || {}),
          [field]: {
            cod: resolved?.codigo || normalizedCode,
            desc: resolved?.descricao || '',
          },
        },
      }));
    } catch {
      // keep code, leave desc empty
    }
  };

  // ── Resolver código de um ListaDicionario ─────────────────────────────────────────
  const handleResolveListCode = async (listKey, index, _targetCodeKey, codeValue) => {
    const group = listConfig[listKey]?.group;
    if (!dictionaryEnabled || !group) {
      setListItem(listKey, index, 'COD', normalizeCode(codeValue));
      return;
    }

    const normalizedCode = normalizeCode(codeValue);
    const resolvingKey = `${listKey}:${index}`;

    if (!normalizedCode) {
      setListItemPatch(listKey, index, { COD: '', DESCRICAO: '' });
      return;
    }

    setResolvingRows((prev) => ({ ...prev, [resolvingKey]: true }));
    try {
      const resolved = await resolveDictionaryValue({
        group,
        code: normalizedCode,
      });
      setListItemPatch(listKey, index, {
        COD: resolved?.codigo || normalizedCode,
        DESCRICAO: resolved?.descricao || '',
      });
    } catch (err) {
      setListItem(listKey, index, 'COD', normalizedCode);
      toast.error(err?.response?.data?.message || 'Erro ao consultar dicionario.');
    } finally {
      setResolvingRows((prev) => {
        const next = { ...prev };
        delete next[resolvingKey];
        return next;
      });
    }
  };

  const getListHandlers = (listKey) => ({
    editable: true,
    onChangeCode: (index, value) => setListItem(listKey, index, 'COD', value),
    onChangeItem: (index, value) => setListItem(listKey, index, 'DESCRICAO', value),
    onResolveCode: (index, value, targetCodeKey) =>
      handleResolveListCode(listKey, index, targetCodeKey, value),
    onAddItem: () => addListItem(listKey),
    onRemoveItem: (index) => removeListItem(listKey, index),
    resolvingKeySet: new Set(
      Object.keys(resolvingRows)
        .filter((k) => k.startsWith(`${listKey}:`))
        .map((k) => Number(k.split(':')[1]))
    ),
  });

  // ── Dicionário modal ───────────────────────────────────────────────────────
  const loadDictionaryTab = async (groupKey, { force = false } = {}) => {
    if (!dictionaryEnabled || !groupKey) return;
    if (!force && Array.isArray(dictionaryData[groupKey])) return;

    setDictionaryLoading((prev) => ({ ...prev, [groupKey]: true }));
    try {
      const list = await listErgoDictionaryItems({ grupo: groupKey });
      setDictionaryData((prev) => ({ ...prev, [groupKey]: list }));
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao carregar dicionario.');
    } finally {
      setDictionaryLoading((prev) => ({ ...prev, [groupKey]: false }));
    }
  };

  const handleOpenDictionaryModal = () => {
    if (!dictionaryEnabled) return;
    setShowDictionaryModal(true);
    loadDictionaryTab(activeDictionaryTab);
  };

  const handleChangeDictionaryCell = (groupKey, index, field, value) => {
    setDictionaryData((prev) => {
      const list = Array.isArray(prev[groupKey]) ? [...prev[groupKey]] : [];
      if (!list[index]) return prev;
      list[index] = { ...list[index], [field]: value };
      return { ...prev, [groupKey]: list };
    });
  };

  const handleSaveDictionaryRow = async (groupKey, row) => {
    const codigo = normalizeCode(row?.codigo);
    const descricao = String(row?.descricao || '').trim();
    if (!codigo || !descricao) {
      toast.warn('Codigo e descricao sao obrigatorios.');
      return;
    }
    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertErgoDictionaryItem({ grupo: groupKey, codigo, descricao });
      toast.success('Dicionario atualizado.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao salvar item do dicionario.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  const handleSaveDictionaryDraft = async (groupKey) => {
    const draft = dictionaryDraft[groupKey] || { codigo: '', descricao: '' };
    const codigo = normalizeCode(draft.codigo);
    const descricao = String(draft.descricao || '').trim();
    if (!codigo || !descricao) {
      toast.warn('Informe codigo e descricao para incluir novo item.');
      return;
    }
    const saveKey = `${groupKey}:${codigo}`;
    try {
      setSavingDictionaryKey(saveKey);
      await upsertErgoDictionaryItem({ grupo: groupKey, codigo, descricao });
      setDictionaryDraft((prev) => ({
        ...prev,
        [groupKey]: { codigo: '', descricao: '' },
      }));
      toast.success('Novo item incluido no dicionario.');
      await loadDictionaryTab(groupKey, { force: true });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao incluir item no dicionario.');
    } finally {
      setSavingDictionaryKey('');
    }
  };

  // ── Gravar ────────────────────────────────────────────────────────────────
  const handleGravar = async () => {
    if (!id) return toast.warn('Erro de validação!');
    try {
      setSaving(true);
      const payload = {
        EXAMEFISICO: formData?.EXAMEFISICO ?? '',
        FCDEITADO: formData?.FCDEITADO ?? '',
        PADEITADO: formData?.PADEITADO ?? '',
        FCPE: formData?.FCPE ?? '',
        PAPE: formData?.PAPE ?? '',
        FREQMAX: formData?.FREQMAX ?? '',
        FREQSUB: formData?.FREQSUB ?? '',
        PAHIPER: formData?.PAHIPER ?? '',
        OBS: formData?.OBS ?? '',
      };

      // Build altECG payload extracting only the code values
      const altECGPayload = {};
      ALL_LEADS.forEach((lead) => {
        ['A', 'E', 'D'].forEach((phase) => {
          const key = `${lead}${phase}`;
          altECGPayload[key] = formData?.altECG?.[key]?.cod ?? '';
        });
      });
      payload.alteracoesECG = [altECGPayload];

      textListKeys.forEach((key) => {
        const apiKey = key === 'conclusoesList' ? 'conclusoes' : key;
        payload[apiKey] = Array.isArray(formData?.[key]) ? formData[key] : [];
      });

      const res = await updateErgo(id, payload);
      await mutate(['ergo', id]);
      toast.success(res?.message || 'Laudo atualizado.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao gravar.');
    } finally {
      setSaving(false);
    }
  };

  const handleVisualizar = () => {
    if (!id) {
      toast.warn('Erro de validação!');
      return;
    }
    history.push(`/visualizacao-ergo/${id}`);
  };
  const handleAnexar = () => toast.info('Em desenvolvimento...');

  // ── Renderização das abas ──────────────────────────────────────────────────
  const renderTabContent = () => {
    if (!formData || !ergo) return null;

    const dataNascFmt = ergo.dataNasc || ergo.dataNascFmt || '-';
    const dataErgoFmt = ergo.dataErgo || ergo.dataErgoFmt || '-';

    return {
      'dados-pessoais': (
        <div className="row g-3 legacy-panel-grid">
          <div className="col-lg-8">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Paciente</div>
              <div className="legacy-field-value">
                {ergo.CODPAC} | {ergo.NOMEPAC}
              </div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Sexo</div>
              <div className="legacy-field-value">{ergo.SEXOPAC || '-'}</div>
            </div>
          </div>
          <div className="col-lg-2">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Idade</div>
              <div className="legacy-field-value">
                {ergo.idadePAC != null ? `${ergo.idadePAC} anos` : '-'}
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Data de Nasc.</div>
              <div className="legacy-field-value">{dataNascFmt}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Convênio</div>
              <div className="legacy-field-value">{ergo.SIGLACONV || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Cod. Ergo</div>
              <div className="legacy-field-value">{ergo.CODEXAME || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Data do Exame</div>
              <div className="legacy-field-value">{dataErgoFmt}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Médico Realizou</div>
              <div className="legacy-field-value">{ergo.Realizou || '-'}</div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="legacy-field-block">
              <div className="legacy-field-label">Médico Solicitou</div>
              <div className="legacy-field-value">{ergo.Solicitou || '-'}</div>
            </div>
          </div>
        </div>
      ),

      'dados-clinicos': (
        <>
          <ListaDicionario
            title={listConfig.indicacoes.title}
            items={formData.indicacoes}
            {...getListHandlers('indicacoes')}
          />
          <ListaDicionario
            title={listConfig.queixas.title}
            items={formData.queixas}
            {...getListHandlers('queixas')}
          />
          <ListaDicionario
            title={listConfig.medicamentos.title}
            items={formData.medicamentos}
            {...getListHandlers('medicamentos')}
          />
          <ListaDicionario
            title={listConfig.fatoresRisco.title}
            items={formData.fatoresRisco}
            {...getListHandlers('fatoresRisco')}
          />
        </>
      ),

      avaliacoes: (
        <>
          <ListaDicionario
            title={listConfig.atividadeFisica.title}
            items={formData.atividadeFisica}
            {...getListHandlers('atividadeFisica')}
          />
          <Form.Group className="mb-3">
            <strong>Exame Físico:</strong>
            <Form.Control
              as="textarea"
              rows={Math.max(3, (String(formData.EXAMEFISICO || '').match(/\n/g) || []).length + 2)}
              className="legacy-observacoes-area mt-1"
              value={formData.EXAMEFISICO}
              onChange={(e) => setField('EXAMEFISICO', e.target.value)}
            />
          </Form.Group>
          <ListaDicionario
            title={listConfig.ecgBasal.title}
            items={formData.ecgBasal}
            {...getListHandlers('ecgBasal')}
          />
        </>
      ),

      'alt-ecg': (
        <div className="table-responsive">
          <div className="d-flex justify-content-center mb-3 gap-4">
            <Form.Check
              inline
              type="radio"
              label="13 Canais"
              id="canais-13"
              checked={altECGCanais === 13}
              onChange={() => setAltECGCanais(13)}
            />
            <Form.Check
              inline
              type="radio"
              label="16 Canais"
              id="canais-16"
              checked={altECGCanais === 16}
              onChange={() => setAltECGCanais(16)}
            />
          </div>
          <Table bordered size="sm" className="small">
            <thead>
              <tr>
                <th style={{ width: '65px' }}>Deriv.</th>
                <th className="text-center">Antes</th>
                <th className="text-center">No Esforço</th>
                <th className="text-center">Após</th>
              </tr>
            </thead>
            <tbody>
              {(altECGCanais === 16 ? ALL_LEADS : LEADS_13).map((lead) => (
                <tr key={lead}>
                  <td className="align-middle">
                    <strong>{lead}:</strong>
                  </td>
                  {['A', 'E', 'D'].map((phase) => {
                    const key = `${lead}${phase}`;
                    const cell = formData.altECG?.[key] || {
                      cod: '',
                      desc: '',
                    };
                    return (
                      <td key={phase}>
                        <div className="d-flex gap-1 align-items-center">
                          <Form.Control
                            type="text"
                            size="sm"
                            style={{
                              width: '46px',
                              minWidth: '46px',
                              textAlign: 'center',
                            }}
                            value={cell.cod}
                            onChange={(e) => setAltECGCell(key, 'cod', e.target.value)}
                            onBlur={(e) => handleResolveAltECGCode(key, e.target.value)}
                          />
                          <Form.Control
                            type="text"
                            size="sm"
                            readOnly
                            tabIndex={-1}
                            value={cell.desc}
                            style={{ fontSize: '11px' }}
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ),

      conclusoes: (
        <>
          <ListaDicionario
            title={listConfig.arritmias.title}
            items={formData.arritmias}
            {...getListHandlers('arritmias')}
          />
          <ListaDicionario
            title={listConfig.comportamentoPA.title}
            items={formData.comportamentoPA}
            {...getListHandlers('comportamentoPA')}
          />
          <div className="row g-3 legacy-panel-grid mb-3">
            <div className="col-sm-4">
              <div className="legacy-field-block">
                <div className="legacy-field-label">Índice Cronotrópico (%)</div>
                <Form.Control
                  type="text"
                  value={formData.PADEITADO}
                  onChange={(e) => setField('PADEITADO', e.target.value)}
                />
              </div>
            </div>
            <div className="col-sm-4">
              <div className="legacy-field-block">
                <div className="legacy-field-label">Escore de Duke</div>
                <Form.Control
                  type="text"
                  value={formData.PAHIPER}
                  onChange={(e) => setField('PAHIPER', e.target.value)}
                />
              </div>
            </div>
          </div>
          <ListaDicionario
            title={listConfig.comentarios.title}
            items={formData.comentarios}
            {...getListHandlers('comentarios')}
          />
          <ListaDicionario
            title={listConfig.conclusoesList.title}
            items={formData.conclusoesList}
            {...getListHandlers('conclusoesList')}
          />
        </>
      ),

      observacao: (
        <Form.Group>
          <strong>Observação:</strong>
          <Form.Control
            as="textarea"
            rows={Math.max(6, (String(formData.OBS || '').match(/\n/g) || []).length + 3)}
            className="legacy-observacoes-area mt-1"
            value={formData.OBS}
            onChange={(e) => setField('OBS', e.target.value)}
          />
        </Form.Group>
      ),
    };
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Card className="mb-3 mt-2 no-print legacy-eco-card">
      <Card.Header className="legacy-header">
        <div className="legacy-header-side legacy-header-side-left">
          <div className="legacy-header-eco-wrap">
            <div className="legacy-mini-label">Ergo N</div>
            <div className="legacy-mini-value">{ergo?.CODEXAME || '-'}</div>
          </div>
        </div>
        <div className="legacy-header-title-wrap text-center">
          <div className="legacy-header-title">Ergometria</div>
        </div>
        <div className="legacy-header-side legacy-header-side-right">
          <div className="legacy-header-date-wrap">
            <div className="legacy-mini-label">Data</div>
            <div className="legacy-mini-value">{ergo?.dataErgo || ergo?.dataErgoFmt || '-'}</div>
          </div>
          {dictionaryEnabled ? (
            <Button
              variant="outline-primary"
              className="legacy-dictionaries-btn"
              onClick={handleOpenDictionaryModal}
            >
              Dicionarios
            </Button>
          ) : null}
        </div>
      </Card.Header>

      {isLoading && (
        <Card.Body>
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Card.Body>
      )}

      {error && !isLoading && (
        <Card.Body className="text-center text-danger py-5">
          {error.message || 'Erro ao carregar o laudo.'}
        </Card.Body>
      )}

      {ergo && !isLoading && !error && formData && (
        <>
          <Card.Body className="legacy-card-body">
            <div className="legacy-content-shell container-fluid px-0">
              <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || activeTab)}>
                <Nav variant="pills" className="legacy-tabs-row legacy-tabs-row-bottom flex-wrap">
                  {bottomTabList.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key} className="legacy-tab-link">
                        {tab.label}
                      </Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>

                <div className="legacy-section-title-wrap">
                  <div className="legacy-section-title">
                    {tabHeadingMap[activeTab] || 'Ergometria'}
                  </div>
                </div>

                <Tab.Content className="legacy-tab-content">
                  {bottomTabList.map((tab) => (
                    <Tab.Pane key={tab.key} eventKey={tab.key}>
                      {renderTabContent()?.[tab.key] || null}
                    </Tab.Pane>
                  ))}
                </Tab.Content>
              </Tab.Container>
            </div>
          </Card.Body>

          <Card.Footer className="legacy-footer no-print d-flex flex-wrap justify-content-center gap-2">
            <Button variant="success" onClick={handleGravar} disabled={saving}>
              {saving ? <Spinner animation="border" size="sm" /> : 'Gravar'}
            </Button>{' '}
            <Button variant="secondary" onClick={handleVisualizar}>
              Visualizar
            </Button>{' '}
            <Button variant="info" onClick={handleAnexar}>
              Anexar
            </Button>
          </Card.Footer>

          {dictionaryEnabled ? (
            <Modal
              size="xl"
              show={showDictionaryModal}
              onHide={() => setShowDictionaryModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>Dicionarios da Ergometria</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Tab.Container
                  activeKey={activeDictionaryTab}
                  onSelect={(k) => {
                    const next = k || activeDictionaryTab;
                    setActiveDictionaryTab(next);
                    loadDictionaryTab(next);
                  }}
                >
                  <Nav variant="tabs" className="legacy-dictionary-tabs flex-wrap">
                    {dictionaryTabList.map((tab) => (
                      <Nav.Item key={tab.key}>
                        <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>

                  <Tab.Content className="legacy-dictionary-content">
                    {dictionaryTabList.map((tab) => {
                      const list = Array.isArray(dictionaryData[tab.key])
                        ? dictionaryData[tab.key]
                        : [];
                      const draft = dictionaryDraft[tab.key] || {
                        codigo: '',
                        descricao: '',
                      };
                      const loading = dictionaryLoading[tab.key];

                      return (
                        <Tab.Pane key={tab.key} eventKey={tab.key}>
                          <div className="legacy-dictionary-new-row">
                            <Form.Control
                              type="text"
                              placeholder="Codigo"
                              value={draft.codigo}
                              onChange={(e) =>
                                setDictionaryDraft((prev) => ({
                                  ...prev,
                                  [tab.key]: {
                                    ...draft,
                                    codigo: e.target.value,
                                  },
                                }))
                              }
                            />
                            <Form.Control
                              type="text"
                              placeholder="Descricao"
                              value={draft.descricao}
                              onChange={(e) =>
                                setDictionaryDraft((prev) => ({
                                  ...prev,
                                  [tab.key]: {
                                    ...draft,
                                    descricao: e.target.value,
                                  },
                                }))
                              }
                            />
                            <Button
                              variant="success"
                              onClick={() => handleSaveDictionaryDraft(tab.key)}
                              disabled={savingDictionaryKey.startsWith(`${tab.key}:`)}
                            >
                              Incluir
                            </Button>
                          </div>

                          {loading ? (
                            <div className="text-center py-4">
                              <Spinner animation="border" size="sm" />
                            </div>
                          ) : (
                            <div className="legacy-dictionary-grid">
                              {list.map((row, index) => {
                                const rowKey = `${tab.key}:${row.codigo}`;
                                return (
                                  <div
                                    className="legacy-dictionary-row"
                                    key={rowKey || `${tab.key}-${index}`}
                                  >
                                    <Form.Control
                                      type="text"
                                      value={row.codigo}
                                      onChange={(e) =>
                                        handleChangeDictionaryCell(
                                          tab.key,
                                          index,
                                          'codigo',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Form.Control
                                      type="text"
                                      value={row.descricao}
                                      onChange={(e) =>
                                        handleChangeDictionaryCell(
                                          tab.key,
                                          index,
                                          'descricao',
                                          e.target.value
                                        )
                                      }
                                    />
                                    <Button
                                      variant="primary"
                                      onClick={() => handleSaveDictionaryRow(tab.key, row)}
                                      disabled={savingDictionaryKey === rowKey}
                                    >
                                      Salvar
                                    </Button>
                                  </div>
                                );
                              })}
                              {list.length === 0 ? (
                                <p className="text-muted mb-0">
                                  Nenhum item encontrado neste dicionario.
                                </p>
                              ) : null}
                            </div>
                          )}
                        </Tab.Pane>
                      );
                    })}
                  </Tab.Content>
                </Tab.Container>
              </Modal.Body>
            </Modal>
          ) : null}
        </>
      )}
    </Card>
  );
}
